// CarrierMarketManagementPage
// This page targets the "Carrier -> Markets" relation as a standalone route.
// It keeps per-tab isolation and persistence by storing:
//   - selected carrier id
//   - grid state
// under the current tab namespace in M_State, rehydrating on tab switch.
//
// Routes that should land here (already configured in your router):
//   /carrier-markets          -> renders CarrierMarketManagementPage
//
// Notes:
// - The title area uses the same i18n key as the backend MenuDisplay value:
//     MenuCarrierMarketsManagementLabel
// - We also keep the tab label in sync with that key so the tab caption matches
//   the lateral menu entry for this sub-menu.
// - The inner content is the original CarrierMarketManagement component, unchanged
//   in behavior (only wrapped with the page shell).

// --------------------------------------------------------------------------------------
// Notes
// - Markets list is NOT preloaded; it's paginated, searched and ordered by SelectableTileGrid.
// - Pre-linked markets (for the selected carrier) appear CHECKED via selectedIds.
// - Toggling a market uses the existing endpoints (POST/DELETE) and updates state accordingly.
// - Per-tab isolation & persistence: persist (a) selected carrier id and (b) the grid state
//      under the current tab namespace, rehydrating on tab switch.
// - Shared look and feel via relation-management.css
// --------------------------------------------------------------------------------------

import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import {
  LoadingContext,
  MButton,
  M_StateActions,
  NavigateContext,
} from 'src/M_Components';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import PropTypes from 'prop-types';
import '../relation-management.css';  // Shared CSS for relation management pages

// Generic, paginated selector
import SelectableTileGrid from '../../components/SelectableTileGrid/SelectableTileGrid';

// Dynamic select box (server-driven, no preloaded options)
import DynamicSelectBox from 'src/components/Forms/DynamicSelectBox';

export default function CarrierMarketManagementPage({ toastMessages }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const matches = useMediaQuery('(min-width:900px)');

  // ------------------------------------------------------------------------------------------------------------
  // Read current selected tab id from global state so we can safely rename the tab caption.
  // ------------------------------------------------------------------------------------------------------------
  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  );

  // Keep a local namespace id that tracks the current tab id when available.
  // This allows us to rehydrate correctly even if selectedTab changes over time.
  const [id, setId] = useState(() => selectedTab);

  // Flag to ensure we always give priority to session restore before default selection logic.
  const [hasRestoredFromSession, setHasRestoredFromSession] = useState(false);

  // ------------------------------------------------------------------------------------------------------------
  // EFFECT
  // Update tab title when language changes. This keeps the tab label aligned with the menu display:
  //   MenuCarrierMarketsManagementLabel (submenu under the Carriers root).
  //
  // IMPORTANT:
  // - This is optional but useful when the user changes language at runtime.
  // - It assumes the tab was initially created using the same menu display key.
  // ------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTab) return;
    renameTab(String(selectedTab), t('MenuCarrierMarketsManagementLabel'));
  }, [selectedTab, renameTab, t]);

  // --- Local state (small & focused) ---------------------------------------------------
  // Removed local carriers array: Carrier select is now fully server-driven by DynamicSelectBox
  const [linkedMarkets, setLinkedMarkets] = useState([]);

  // Per-tab namespace + isolated selection
  const [selectedCarrierId, setSelectedCarrierId] = useState(null);   // store only the id

  // Keys inside the tab namespace (keep short & explicit)
  const K = {
    selectedCarrierId: 'CMM.selectedCarrierId',
    gridMarkets: 'CMM.grid.markets',
  };

  // Helpers to read/write a single key under the current tab namespace
  const upsertSession = (tabId, kv) => {
    const current = M_State.find(o => o.key === tabId)?.value || [];
    const filtered = current.filter(e => e.key !== kv.key);
    dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
  };
  const pick = (arr, k) => (arr || []).find(x => x.key === k)?.value;

  // Stable base url + buffers
  const baseUrl = ctx.DEFAULTURL;
  const carriersBuffer = useMemo(() => new BufferService('Carriers', baseUrl), [baseUrl]);

  // ============================================================================================================
  // Rehydrate per-tab state (tab switch or first mount)
  // - We resolve the current tab id from selectedTab or local id.
  // - This keeps per-tab isolation while still allowing re-opened tabs to recover their state.
  // - This effect is also responsible for deciding if there is a saved carrier id before
  //   we fall back to default-first-carrier logic.
  // ============================================================================================================
  useEffect(() => {
    const currentTabId = selectedTab || id;
    if (!currentTabId) return;

    if (currentTabId !== id) {
      setId(currentTabId);
    }

    const session = M_State.find(obj => obj.key === currentTabId)?.value;

    if (session) {
      const savedCarrierId = pick(session, K.selectedCarrierId);
      if (savedCarrierId !== undefined && savedCarrierId !== null) {
        setSelectedCarrierId(savedCarrierId);
        setHasRestoredFromSession(true);
        return;
      }
    }

    // Even if there is no session or no saved carrier id, we mark that restoration was attempted.
    setHasRestoredFromSession(true);
  }, [M_State, selectedTab, id]);

  // Persist selected carrier id per-tab
  useEffect(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return;
    upsertSession(currentTabId, { key: K.selectedCarrierId, value: selectedCarrierId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedTab, selectedCarrierId]);

  // ============================================================================================================
  // Initial selection strategy for Carrier
  // ============================================================================================================
  // No preloaded options. Use DynamicSelectBox to fetch. For default behavior:
  // 1) If per-tab state already set, keep it (restored above).
  // 2) Else fetch the first page (PageSize=1, active only, ordered by Name asc) and use first Id, if any.
  useEffect(() => {
    // Wait until we have tried to restore from session, so saved state always wins.
    if (!hasRestoredFromSession) return;

    // If already selected (from session), do nothing
    if (selectedCarrierId != null) return;

    (async () => {
      try {
        const order = encodeURIComponent('Name asc');
        const filter = encodeURIComponent('Inactive eq false');
        const res = await carriersBuffer.filter(`?Page=1&PageSize=1&OrderBy=${order}&Filter=${filter}`);
        if (isFailurePayload(res)) return;
        const items = Array.isArray(res?.Items) ? res.Items : (Array.isArray(res) ? res : []);
        const first = items?.[0];
        if (first?.Id != null) setSelectedCarrierId(first.Id);
      } catch {
        // silent fallback
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRestoredFromSession, carriersBuffer, selectedCarrierId]);

  // ============================================================================================================
  // Load existing relations when a Carrier is selected
  // ============================================================================================================
  useEffect(() => {
    if (!selectedCarrierId) {
      setLinkedMarkets([]);
      return;
    }
    ctx.setIsLoad(true);
    const carrierMarketsBuffer = new BufferService(`Carriers/${selectedCarrierId}/Markets`, baseUrl);

    carrierMarketsBuffer
      .get()
      .then((data) => {
        if (isFailurePayload(data)) {
          toastMessages('error', '', apiMessage(data, t('ErrorLoadingData')));
          return;
        }
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.Items) ? data.Items : []);
        setLinkedMarkets(arr.map((m) => m.Id).filter((v) => v != null));
      })
      .catch((err) => toastMessages('error', '', apiMessage(err, t('ErrorLoadingData'))))
      .finally(() => ctx.setIsLoad(false));
  }, [selectedCarrierId, baseUrl]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================================================
  // Toggle relation (checked => remove; unchecked => add)
  // ============================================================================================================
  const toggleMarket = (marketId) => {
    if (!selectedCarrierId) return;
    ctx.setIsLoad(true);

    const isLinked = linkedMarkets.includes(marketId);
    const addBuffer = new BufferService(`Carriers/${selectedCarrierId}/Markets`, baseUrl);
    const removeBuffer = new BufferService(`Carriers/${selectedCarrierId}/Markets`, baseUrl);

    const request = isLinked
      ? removeBuffer.delete(marketId)
      : addBuffer.post({ marketId });

    request
      .then((res) => {
        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }
        setLinkedMarkets((prev) =>
          isLinked
            ? prev.filter((id) => id !== marketId)
            : [...prev, marketId]
        );
        toastMessages('success', '', isLinked ? t('MarketRemoved') : t('MarketAdded'));
      })
      .catch((err) => toastMessages('error', '', apiMessage(err, t('ErrorSaving'))))
      .finally(() => ctx.setIsLoad(false));
  };

  // ============================================================================================================
  // Read persisted grid state (per tab) for initialState
  // ============================================================================================================
  const gridInitialState = useMemo(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return undefined;
    const session = M_State.find((o) => o.key === currentTabId)?.value || [];
    return pick(session, K.gridMarkets) || undefined;
  }, [M_State, id, selectedTab]);

  // ============================================================================================================
  // Render
  // ============================================================================================================
  return (
    <div className="divGlobalSettings" style={{ padding: '20px' }}>
      {/* Title area shows the section title based on i18n keys */}
      <div
        className="titleSettings"
        style={{
          color: ctx.secondaryColor,
          padding: '20px',
          marginBottom: '20px',
        }}
      >
        {t('MenuCarrierMarketsManagementLabel')}
      </div>

      {/* Keep layout responsive as before. The main content is the carrier/markets relation management. */}
      <div style={matches ? { display: 'flex' } : {}}>
        <div className="settingsPageMainDiv" style={{ width: '100%' }}>
          <div className="firstDivContainer rel-card">
            {/* Carrier select (server-driven via DynamicSelectBox, active only, no preloaded options) */}
            <div className="rel-section">
              <span className="rel-title">{t('Carrier')}</span>
              <DynamicSelectBox
                label={t('Carrier')}
                tableName="Carriers"
                groupBy="Name"
                valueId={selectedCarrierId}
                required
                pageSize={20}
                onChange={(opt) => setSelectedCarrierId(opt?.value ?? null)}
              />
            </div>

            {/* Markets grid (only after selecting a carrier) */}
            {selectedCarrierId && (
              <div className="rel-grid-section">
                <div className="rel-grid-box-500-700">
                  <SelectableTileGrid
                    /* Business behavior:
                       - Paginated markets (server-side): endpoint="Markets"
                       - Only show active markets (staticFilter)
                       - Already-linked ones appear CHECKED (selectedIds=linkedMarkets)
                       - Toggling add/removes the relation with the selected carrier */
                    title={t('Markets')}
                    endpoint="Markets"
                    idField="Id"
                    displayField="Name"
                    pageSize={20}
                    queryable
                    orderable
                    staticFilter="Inactive eq false"
                    selectedIds={linkedMarkets}
                    onItemToggle={(item, next) => {
                      const idVal = item?.Id ?? item?.id;
                      if (idVal != null) toggleMarket(idVal);
                    }}
                    /* Per-tab persistence of grid state (search/order/pages) */
                    stateScopeId={`${id || selectedTab || 'unknown'}::CMM::markets`}
                    initialState={gridInitialState}
                    onStateChange={(st) => {
                      const currentTabId = id || selectedTab;
                      if (!currentTabId) return;
                      upsertSession(currentTabId, { key: K.gridMarkets, value: st });
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

CarrierMarketManagementPage.propTypes = {
  toastMessages: PropTypes.func.isRequired,
};
